import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/Logo';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await login(email, password);
      const to = loc.state?.from || '/dashboard';
      nav(to, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex bg-ink text-white p-12 relative kc-noise">
        <div className="absolute inset-0 gold-sheen pointer-events-none" />
        <div className="relative flex flex-col justify-between w-full">
          <Logo invert />
          <div>
            <h1 className="font-display uppercase text-5xl xl:text-6xl leading-none">Welcome back<br/><span className="text-[color:var(--kc-gold)]">to your headspace.</span></h1>
            <p className="mt-4 text-white/80 max-w-md">Pick up exactly where you left off. Your streak, journal, and progress are waiting.</p>
            <p className="mt-10 text-xs tracking-[0.18em] uppercase text-white/60">Live Life. <span className="text-[color:var(--kc-gold)]">Do Good.</span> Be Happy.</p>
          </div>
          <div />
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-6"><Logo /></div>
          <h2 className="font-display uppercase text-3xl md:text-4xl">Log in</h2>
          <p className="mt-1 text-muted-kc text-sm">Continue your journey.</p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="login-email-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Password</label>
              <div className="relative">
                <input type={show ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-11 px-3 pr-10 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-kc" data-testid="login-toggle-pw">{show ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
              </div>
            </div>
            {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={loading} className="w-full h-11 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors disabled:opacity-60" data-testid="login-submit-button">{loading ? 'Logging in…' : 'Log in'}</button>
          </div>
          <div className="mt-4 text-sm text-right">
            <Link to="/forgot-password" className="text-muted-kc hover:text-ink font-semibold transition-colors" data-testid="login-forgot">Forgot password?</Link>
          </div>
          <div className="mt-2 text-sm text-muted-kc">No account? <Link to="/signup" className="font-semibold text-ink hover:text-gold transition-colors" data-testid="login-go-signup">Create one</Link></div>
        </form>
      </div>
    </div>
  );
}
